/**
 * Presentation-only formatting helpers for Delivery SLA. Kept separate from the
 * mapping layer (mapSlaResponse) so the view models stay data-only.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** ISO date → "28 Jul". Returns '' for missing/invalid input. */
export const formatEddDate = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
};

/** Day-bucket SLA codes ("2DD", "3DD"…) get the weekday appended. */
const isMultiDaySla = (code: string): boolean => /^\d+DD$/.test(code);

/**
 * Delivery copy for an express option, taken from the SLA labels on
 * multiSellerSlaConfig rather than derived from the timestamp:
 *   "4HR"   → "4 hours"           (as configured)
 *   "2DD"   → "2 Days, Fri"       (configured copy + EDD weekday)
 *   "90MIN" → "90 minutes, Mon"   (with `withWeekday`)
 * Returns '' when the code is missing from the config, so the caller falls
 * back to the plain EDD date.
 *
 * `withWeekday` forces the weekday on for codes that don't already carry one:
 * priority rows name the delivery day whatever the bucket code says.
 */
export const formatSlaCode = (
  deliverySLA?: string,
  labels?: Record<string, string>,
  eddUpper?: string,
  withWeekday = false
): string => {
  const code = String(deliverySLA || '');
  const label = code && labels?.[code];
  if (!label) return '';
  if (!isMultiDaySla(code) && !withWeekday) return label;

  const date = eddUpper ? new Date(eddUpper) : null;
  if (!date || Number.isNaN(date.getTime())) return label;
  return `${label}, ${DAYS[date.getDay()]}`;
};

/** ISO date → "Order by 05:30PM" style cutoff copy. Returns '' when absent. */
export const formatCutoff = (iso?: string): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const hours = date.getHours();
  const minutes = date
    .getMinutes()
    .toString()
    .padStart(2, '0');
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const hour12 = (hours % 12 || 12).toString().padStart(2, '0');
  return `Order by ${hour12}:${minutes}${suffix}`;
};
